/**
 * @fileoverview This file contains the PwaManagerService, responsible for managing
 * Progressive Web App (PWA) functionality.
 */

import {delay, map, Observable} from 'rxjs';
import {computed, inject, Injectable, signal, Signal, WritableSignal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {AppInfoStore} from '@cron/data-access';
import {Store} from '@ngrx/store';
import {DeviceService} from '@shared/device';

@Injectable({
	providedIn: 'root',
})
export class PwaManagerService {
	/* ------------------------------- PROVIDERS / SERVICES ------------------------------- */
	private readonly store: Store = inject(Store);
	private readonly deviceService: DeviceService = inject(DeviceService);
	private readonly appInfoStore: AppInfoStore = inject(AppInfoStore);
	/* ------------------------------------ State --------------------------------------- */
	private broadcastChannel = new BroadcastChannel('pwa-channel');
	public deferredPrompt: any;
	public readonly pwaModeObs$: Observable<'standalone' | 'browser'> = pwaModeObservable();
	// Indicate if the PWA is running in standalone mode:
	public isPwaMode$: Signal<boolean> = toSignal(this.pwaModeObs$.pipe(map(mode => mode === 'standalone')));

	// indicates if the PWA can be installed (deferredPrompt is available)
	public readonly isIos$: Signal<boolean> = computed(() => this.deviceService.deviceOS() === 'ios');
	public readonly canInstallPwaApp$: WritableSignal<boolean> = signal(false);
	/* -------------------------------------------  Connection status ------------------------------------------- */
	public readonly appConnection$: Observable<'online' | 'offline'> = appConnectionObservable();
	public readonly isOnline$: Signal<boolean> = toSignal(this.appConnection$.pipe(map(status => status === 'online')));
	public readonly isOffline$: Signal<boolean> = toSignal(this.appConnection$.pipe(map(status => status === 'offline')));
	/* --------------------------------- Constructor ------------------------------------ */
	constructor() {
		setupEventListeners(this);
		this.initializePwaStatus();
	}

	/* ---------------------------- Private Methods ------------------------------------- */
	public handleBeforeInstallPrompt(event: Event): void {
		event.preventDefault();
		this.deferredPrompt = event;
		this.canInstallPwaApp$.set(true);
	}

	public handleAppInstalled(): void {
		console.log('App was installed');
		this.setAppInfoIndicator(true);
		this.canInstallPwaApp$.set(false);
	}

	private initializePwaStatus(): void {
		this.pwaModeObs$.pipe(delay(500)).subscribe(pwaMode => {
			this.setAppInfoIndicator(pwaMode === 'standalone');
		});
	}

	private setAppInfoIndicator(isPwaInstalled: boolean): void {
		// this.store.dispatch(appInfoActions.setAppInfoIndicator({isPwaInstalled}));
		this.appInfoStore.setAppInfoIndicator(isPwaInstalled);
	}

	/* ---------------------------- Public Methods ------------------------------------- */
	/**
	 * Checks if PWA is supported.
	 * @returns {boolean} True if PWA is supported, false otherwise.
	 */
	public isPwaSupported(): boolean {
		return !!this.deferredPrompt;
	}

	/**
	 * Initiates the PWA installation process.
	 * @returns {Promise<void>}
	 */
	public async installPwa(): Promise<void> {
		if (!this.deferredPrompt) {
			console.log('PWA installation prompt not available');
			return;
		}

		try {
			this.deferredPrompt.prompt();
			const choiceResult = await this.deferredPrompt.userChoice;
			console.log(`User ${choiceResult.outcome === 'accepted' ? 'accepted' : 'dismissed'} the A2HS prompt`);
		} catch (error) {
			console.error('Error during PWA installation:', error);
		} finally {
			this.deferredPrompt = null;
		}
	}
}

/* -------------------------------------------------------------------------------------------------------------- */
let deferredPrompt: any;
function setupEventListeners(pwaManagerService?: PwaManagerService): void {
	if (!window) return;
	if (pwaManagerService && !pwaManagerService.deferredPrompt && deferredPrompt) {
		pwaManagerService.deferredPrompt = deferredPrompt;
		pwaManagerService.handleBeforeInstallPrompt(deferredPrompt);
	}
	window.addEventListener('beforeinstallprompt', e => {
		e.preventDefault();
		deferredPrompt = e;
	});
	window.addEventListener('appinstalled', e => {
		if (pwaManagerService) {
			pwaManagerService?.handleAppInstalled();
		}
	});
}
setupEventListeners();

/* -------------------------------------------------------------------------------------------------------------- */

function appConnectionObservable(): Observable<'online' | 'offline'> {
	return new Observable<'online' | 'offline'>(subscriber => {
		const listener = () => subscriber.next(navigator.onLine ? 'online' : 'offline');
		window.addEventListener('online', listener);
		window.addEventListener('offline', listener);
		listener(); // Emit initial state
		return () => {
			window.removeEventListener('online', listener);
			window.removeEventListener('offline', listener);
		};
	});
}

function pwaModeObservable(): Observable<'standalone' | 'browser'> {
	return new Observable<'standalone' | 'browser'>(subscriber => {
		const listener = () => {
			subscriber.next(window.matchMedia('(display-mode: standalone)').matches ? 'standalone' : 'browser');
		};
		window.matchMedia('(display-mode: standalone)').addEventListener('change', listener);
		listener(); // Emit initial state
		return () => window.matchMedia('(display-mode: standalone)').removeEventListener('change', listener);
	});
}
